import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { useMutation } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useAmplitudeExperiment } from "./useAmplitudeExperiment";
import type {
  DeleteAccountMutation,
  DeleteAccountMutationVariables,
  RegisterMutation,
  RegisterMutationVariables,
} from "@/codegen/graphql/generated/graphql";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { UserFragment } from "@/codegen/graphql/fragments/User.fragment";
import { useAuthStore } from "@/stores/useAuth";
import { evictUserScopedData } from "@/api/userScopedCache";
import { REGISTER } from "@/codegen/graphql/mutations/Register.mutation";
import { DELETE_ACCOUNT } from "@/codegen/graphql/mutations/DeleteAccount.mutation";
import { clearPremiumGracePeriod } from "@/lib/premiumGracePeriod";
import Tracking from "@/lib/tracking";

const fpPromise = FingerprintJS.load();

export type AuthWithFragment = {
  token: string;
  user: ResultOf<typeof UserFragment>;
};

const useAuth = () => {
  const resetAuthStore = useAuthStore((s) => s.resetAuthStore);
  const { setUserABTestVariants } = useAmplitudeExperiment();

  const [register] = useMutation<RegisterMutation, RegisterMutationVariables>(
    REGISTER,
  );
  const [deleteAccountMutation] = useMutation<
    DeleteAccountMutation,
    DeleteAccountMutationVariables
  >(DELETE_ACCOUNT);

  const getFingerprint = async () => {
    const fp = await fpPromise;
    const result = await fp.get();
    return result.visitorId + "." + new Date().getTime();
  };

  /** Writes the user the API answered: the store, the analytics identity and the Sentry context. */
  const storeUser = (auth: AuthWithFragment) => {
    const userData = auth.user;
    const previousUserId = useAuthStore.getState().currentUser?.id;
    useAuthStore.setState({
      accessToken: auth.token,
      currentUser: userData,
    });
    // Another account answers the per-user fields now (SHO-827); linking a
    // provider to the anonymous user keeps the id. After the token, so the
    // refetches the eviction triggers carry the new one. The premium grace
    // period was the previous account's purchase, not this one's.
    if (previousUserId !== userData.id) {
      evictUserScopedData();
      clearPremiumGracePeriod();
    }
    Tracking.setUserId(userData.trackingId);

    const deviceId = useAuthStore.getState().deviceId;

    Sentry.setUser({
      id: userData.id,
      username: userData.name || undefined,
      email: userData.email || undefined,
      deviceId: deviceId || undefined,
    });
  };

  /** A login: the user changed, so their variants follow. */
  const fillUser = (auth: AuthWithFragment) => {
    storeUser(auth);
    return setUserABTestVariants(auth.user.id);
  };

  // The device user: no variants read of its own, the boot's goes through
  // the gate in `__root.tsx` and the logout's follows below (SHO-852).
  const registerUser = async (uid: string) => {
    const { data } = await register({
      variables: {
        input: { uid },
      },
    });
    if (data?.register.token) storeUser(data.register as AuthWithFragment);
  };

  const logoutUser = async () => {
    resetAuthStore();
    clearPremiumGracePeriod();

    // Clear Sentry user context on logout
    Sentry.setUser(null);

    const fingerprint = await getFingerprint();
    await registerUser(fingerprint);
    void setUserABTestVariants();
  };

  const deleteAccount = async () => {
    await deleteAccountMutation();

    await logoutUser();
  };

  return { fillUser, logoutUser, deleteAccount, registerUser };
};

export default useAuth;
